import { useReadScenarioApprovalsById } from "@/libs/react-query/hooks/useScenarioApprovalsQuery";
import { useNavigate, useParams } from "react-router-dom";
import ContentPasteGoPng from "@/assets/images/content-paste-go-rounded.png";
import CommonImg from "@/components/Common/Img";
import { SCENARIO_DECISION_LABELS, ScenarioDecision } from "@/consts/Scenario";
import useUserStore from "@/stores/User";
import { CurrentUserContent } from "@/types/setting";
import { useReadProjectGroups } from "@/libs/react-query/hooks/useProjectQuery";
import CancelApprovalDialog from "./CancelApprovalDialog";
import ScenarioHeaderActions from "./ScenarioHeaderActions";
import { Scenario } from "@/types/scenario";
import { useIsFinancialManager } from "@/hooks/useIsFinancialManager";

type Props = {
  approval_id: string;
  scenario: Scenario;
  scenarios: Scenario[];
};

export default function ScenarioHeaderStatus({
  approval_id,
  scenario,
  scenarios,
}: Props) {
  const { group_id = "" } = useParams();
  const navigate = useNavigate();

  const storedUser: CurrentUserContent = useUserStore((state) => state.user);
  const isFinancialManager = useIsFinancialManager();

  const { data } = useReadScenarioApprovalsById(approval_id);
  const { data: { users = [] } = {} } = useReadProjectGroups(group_id);

  if (!data) {
    return null;
  }

  // 결재 상세 페이지 접근 가능 여부 체크
  const canAccessApprovalPage = () => {
    if (!data || !storedUser) return false;

    // 프로젝트에 참여중인 사용자 전체에게 접근 가능
    return users.some((user) => user.user_id === storedUser.user_id);
  };

  // 승인 요청자인지 확인
  const isRequestedByMe = data.requested_by.user_id === storedUser.user_id;

  // 승인이 완료되지 않은 상태인지 확인
  const isApprovalPending = data.final_decision === ScenarioDecision.UNDECIDED;

  const renderApprovalLabel = () => {
    if (!data) return null;
    const { final_decision } = data;

    if (final_decision === ScenarioDecision.APPROVED) {
      return (
        <div className="text-sm text-green font-normal">
          {SCENARIO_DECISION_LABELS[final_decision]}
        </div>
      );
    } else if (final_decision === ScenarioDecision.REJECTED) {
      return (
        <div className="text-sm text-pink font-normal">
          {SCENARIO_DECISION_LABELS[final_decision]}
        </div>
      );
    } else {
      return <div className="text-sm text-gray-B font-normal">진행 중</div>;
    }
  };

  return (
    <div className="flex items-center gap-2">
      <span className="text-gray-A text-sm font-semibold">{`승인 단계 ${data.current_stage}`}</span>
      {renderApprovalLabel()}

      {/* 승인 내역 보기 버튼 */}
      {canAccessApprovalPage() && (
        <button
          onClick={() => navigate(`/approvals/${approval_id}`)}
          className="py-[6px] px-3 flex items-center gap-2 bg-blue-ThermoFisher rounded-md"
        >
          <CommonImg src={ContentPasteGoPng} alt="Go" />
          <span className="text-sm text-white font-semibold">
            시나리오 승인 내역
          </span>
        </button>
      )}

      {/* 승인 요청 취소 버튼 - 요청자이고 승인이 진행 중일 때만 표시 */}
      {isRequestedByMe && isApprovalPending && (
        <CancelApprovalDialog approvalId={approval_id} scenario={scenario}>
          <button className="py-[6px] px-3 flex items-center gap-2 bg-pink rounded-md hover:bg-pink/80">
            <span className="text-sm text-white font-semibold">
              승인 요청 취소
            </span>
          </button>
        </CancelApprovalDialog>
      )}

      {/* 시나리오 재생성 액션 - 승인이 완료된 경우 표시 */}
      {data.final_decision !== ScenarioDecision.UNDECIDED && (
        <div className="ml-2">
          <ScenarioHeaderActions scenario={scenario} scenarios={scenarios} />
        </div>
      )}
    </div>
  );
}
